import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PackageSearch, Search, Edit3, Copy, Trash2, Trophy, Calculator, Package,
  ShoppingBag, Music2, Store, X, Layers, Download, RefreshCw,
} from 'lucide-react';
import { AccountingLayout } from '../components/AccountingLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatRp } from '../lib/format';
import * as XLSX from 'xlsx-js-style';
import {
  listMarketplaceProducts, deleteMarketplaceProduct, duplicateMarketplaceProduct,
  listMarketplaceBrands, recalcMarketplaceProduct, recalcAllMarketplaceProducts,
} from '../lib/api';
import { PRODUCT_TYPES, getAllProductTypes } from './CalculatorPage';

const META = {
  shopee: { label: 'Shopee',     icon: ShoppingBag, chip: 'bg-orange-50 text-orange-800 border-orange-200' },
  tiktok: { label: 'TikTok New', icon: Music2,      chip: 'bg-slate-50 text-slate-800 border-slate-200' },
  minimo: { label: 'Minimo',     icon: Store,       chip: 'bg-blue-50 text-blue-800 border-blue-200' },
};

const TYPE_BADGE = {
  raket:       'bg-emerald-50 text-emerald-800 border-emerald-200',
  senar:       'bg-purple-50 text-purple-800 border-purple-200',
  grip:        'bg-yellow-50 text-yellow-800 border-yellow-200',
  shuttlecock: 'bg-sky-50 text-sky-800 border-sky-200',
  baju:        'bg-rose-50 text-rose-800 border-rose-200',
  celana:      'bg-indigo-50 text-indigo-800 border-indigo-200',
  aksesoris:   'bg-pink-50 text-pink-800 border-pink-200',
  lainnya:     'bg-stone-100 text-stone-700 border-stone-300',
};
const TYPE_LABEL = new Proxy(
  Object.fromEntries(PRODUCT_TYPES.map((t) => [t.value, t.label])),
  {
    get(target, key) {
      if (key in target) return target[key];
      const all = getAllProductTypes();
      return all.find((t) => t.value === key)?.label
        || (typeof key === 'string' ? key.charAt(0).toUpperCase() + key.slice(1) : '—');
    },
  }
);

const formatDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
};

const rangeText = (values) => {
  const nums = values.filter((v) => v > 0);
  if (!nums.length) return '—';
  const min = Math.min(...nums), max = Math.max(...nums);
  return min === max ? formatRp(min) : `${formatRp(min)} – ${formatRp(max)}`;
};

const bestProfitPerProduct = (p) => {
  let best = -Infinity;
  for (const v of (p.variants || [])) {
    for (const k of ['shopee', 'tiktok', 'minimo']) {
      if ((v?.[k]?.profit || 0) > best) best = v[k].profit || 0;
    }
  }
  return best === -Infinity ? 0 : best;
};

export default function MarketplaceProductsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  const [brandOptions, setBrandOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await listMarketplaceProducts(undefined, typeFilter, brandFilter);
      setProducts(r);
    } catch { toast.error('Gagal memuat produk'); }
    finally { setLoading(false); }
  };

  const refreshBrands = async () => {
    try { setBrandOptions(await listMarketplaceBrands()); }
    catch { setBrandOptions([]); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [typeFilter, brandFilter]);
  useEffect(() => { refreshBrands(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [products, query]);

  const stats = useMemo(() => {
    const total = products.length;
    if (!total) return { total: 0, avgShopee: 0, avgTiktok: 0, avgMinimo: 0, top: null };
    const sums = { shopee: 0, tiktok: 0, minimo: 0 };
    let count = 0;
    for (const p of products) {
      for (const v of (p.variants || [])) {
        count += 1;
        sums.shopee += v?.shopee?.profit || 0;
        sums.tiktok += v?.tiktok?.profit || 0;
        sums.minimo += v?.minimo?.profit || 0;
      }
    }
    const top = products.reduce((best, p) =>
      bestProfitPerProduct(p) > (best ? bestProfitPerProduct(best) : -Infinity) ? p : best, null);
    return {
      total,
      avgShopee: count ? sums.shopee / count : 0,
      avgTiktok: count ? sums.tiktok / count : 0,
      avgMinimo: count ? sums.minimo / count : 0,
      top,
    };
  }, [products]);

  const onEdit = (p) => navigate(`/kalkulator?edit=${p.id}`);
  const onDuplicate = async (p) => {
    try {
      const r = await duplicateMarketplaceProduct(p.id);
      toast.success('Produk diduplikasi');
      await refresh();
      navigate(`/kalkulator?edit=${r.id}`);
    } catch { toast.error('Gagal menduplikasi'); }
  };
  const onRecalc = async (p) => {
    if (!window.confirm(`Hitung ulang "${p.name}" dengan tarif terbaru (termasuk PPh)? Harga jual tidak berubah, profit dihitung ulang. Data lama dibackup.`)) return;
    try {
      await recalcMarketplaceProduct(p.id);
      toast.success('Produk dihitung ulang dengan tarif terbaru');
      await refresh();
    } catch { toast.error('Gagal menghitung ulang'); }
  };
  const [recalcAll, setRecalcAll] = useState(false);
  const onRecalcAll = async () => {
    if (!window.confirm('Hitung ulang SEMUA produk yang belum memakai tarif terbaru (termasuk PPh)? Harga jual tidak berubah, hanya profit & potongan yang dihitung ulang. Data lama otomatis dibackup.')) return;
    setRecalcAll(true);
    try {
      const r = await recalcAllMarketplaceProducts();
      toast.success(r.updated ? `${r.updated} dari ${r.total} produk dihitung ulang (tarif versi ${r.settings_version})` : 'Semua produk sudah memakai tarif terbaru');
      await refresh();
    } catch { toast.error('Gagal menghitung ulang semua produk'); } finally { setRecalcAll(false); }
  };
  const onDelete = async () => {
    if (!confirmDel) return;
    try {
      await deleteMarketplaceProduct(confirmDel.id);
      toast.success('Produk dihapus');
      setConfirmDel(null);
      refresh();
      refreshBrands();
    } catch { toast.error('Gagal menghapus'); }
  };

  const exportExcel = () => {
    if (!filtered.length) { toast.error('Tidak ada produk untuk diekspor'); return; }
    const headers = [
      'Jenis', 'Merek', 'Produk', 'Paket', 'Modal Dasar', 'Tambahan Modal', 'Total Modal',
      'Shopee Harga', 'Shopee Profit', 'Shopee Margin (%)',
      'TikTok Harga',  'TikTok Profit',  'TikTok Margin (%)',
      'Minimo Harga',  'Minimo Profit',  'Minimo Margin (%)',
      'Diperbarui',
    ];
    // Body rows as arrays for aoa_to_sheet (keeps numeric type on numbers)
    const body = [];
    for (const p of filtered) {
      const baseModal = p.base_cost || p.modal || 0;
      for (const v of (p.variants || [])) {
        const total = baseModal + (v.addon_cost || 0);
        body.push([
          TYPE_LABEL[p.product_type || 'lainnya'] || 'Lainnya',
          p.brand || '',
          p.name || '',
          v.name || 'Standar',
          baseModal, v.addon_cost || 0, total,
          v.shopee_selling_price || 0, v?.shopee?.profit || 0, v?.shopee?.margin || 0,
          v.tiktok_selling_price || 0, v?.tiktok?.profit || 0, v?.tiktok?.margin || 0,
          v.minimo_selling_price || 0, v?.minimo?.profit || 0, v?.minimo?.margin || 0,
          formatDate(p.updated_at),
        ]);
      }
    }
    const ws = XLSX.utils.aoa_to_sheet([headers, ...body]);
    // Column widths (in chars)
    ws['!cols'] = [
      { wch: 12 }, { wch: 14 }, { wch: 32 }, { wch: 20 },
      { wch: 14 }, { wch: 14 }, { wch: 14 },
      { wch: 14 }, { wch: 14 }, { wch: 12 },
      { wch: 14 }, { wch: 14 }, { wch: 12 },
      { wch: 14 }, { wch: 14 }, { wch: 12 },
      { wch: 14 },
    ];
    // Excel number formats
    const rpFmt = '"Rp"#,##0;[Red]-"Rp"#,##0';
    const pctFmt = '0.0"%"';
    const rpCols = [4, 5, 6, 7, 8, 10, 11, 13, 14];   // 0-indexed (shifted +1 due to Merek)
    const pctCols = [9, 12, 15];
    const range = XLSX.utils.decode_range(ws['!ref']);
    // Header row bold + fill
    for (let c = 0; c <= range.e.c; c += 1) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
      if (cell) {
        cell.s = {
          font: { bold: true, color: { rgb: 'FFFFFFFF' } },
          fill: { patternType: 'solid', fgColor: { rgb: 'FF065F46' } },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }
    }
    // Number format on body rows
    for (let r = 1; r <= range.e.r; r += 1) {
      for (const c of rpCols) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (cell && typeof cell.v === 'number') cell.z = rpFmt;
      }
      for (const c of pctCols) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (cell && typeof cell.v === 'number') cell.z = pctFmt;
      }
    }
    // Freeze header
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Produk');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `daftar-produk-${stamp}.xlsx`);
    toast.success(`${body.length} baris diekspor`);
  };

  return (
    <AccountingLayout fullWidth>
      <div className="space-y-6" data-testid="produk-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-emerald-800/10 flex items-center justify-center">
              <PackageSearch className="w-5 h-5 text-emerald-800" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">Daftar Produk</h1>
              <p className="text-sm text-stone-500">
                Semua produk yang tersimpan dari Kalkulator Marketplace.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={onRecalcAll} disabled={recalcAll} className="gap-1.5"
              data-testid="btn-recalc-all">
              <RefreshCw className={`w-4 h-4 ${recalcAll ? 'animate-spin' : ''}`} />
              {recalcAll ? 'Menghitung…' : 'Perbarui Semua ke Tarif Terbaru'}
            </Button>
            <Button variant="outline" onClick={exportExcel} className="gap-1.5"
              data-testid="btn-export">
              <Download className="w-4 h-4" /> Ekspor Excel
            </Button>
            <Button onClick={() => navigate('/kalkulator')} className="bg-emerald-800 hover:bg-emerald-900 text-white gap-1.5"
              data-testid="btn-tambah-produk">
              <Calculator className="w-4 h-4" /> Buka Kalkulator
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total Produk" value={stats.total} icon={Package} accent="emerald" />
          <StatCard label="Avg Profit Shopee (per paket)" value={formatRp(Math.round(stats.avgShopee))} icon={ShoppingBag} accent="orange" />
          <StatCard label="Avg Profit TikTok (per paket)" value={formatRp(Math.round(stats.avgTiktok))} icon={Music2} accent="slate" />
          <StatCard label="Avg Profit Minimo (per paket)" value={formatRp(Math.round(stats.avgMinimo))} icon={Store} accent="blue" />
        </div>
        {stats.top && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-emerald-800" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold">
                Produk Profit Tertinggi
              </div>
              <div className="text-lg font-semibold text-stone-900 truncate">{stats.top.name}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] text-stone-500">Max Profit</div>
              <div className="text-lg font-mono font-bold text-emerald-900">
                {formatRp(bestProfitPerProduct(stats.top))}
              </div>
            </div>
          </div>
        )}

        {/* Filter Tabs (Jenis) */}
        <div className="flex items-center gap-1 flex-wrap">
          {[{ value: 'all', label: 'Semua' }, ...getAllProductTypes()].map((t) => (
            <button key={t.value}
              onClick={() => setTypeFilter(t.value)}
              className={`text-xs px-3 py-1.5 rounded-md border transition ${typeFilter === t.value
                ? 'bg-emerald-800 text-white border-emerald-800'
                : 'bg-white text-stone-700 border-stone-200 hover:border-emerald-300'}`}
              data-testid={`filter-${t.value}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Filter Merek */}
        {brandOptions.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mr-2">
              Merek
            </span>
            <button
              onClick={() => setBrandFilter('all')}
              className={`text-xs px-3 py-1.5 rounded-md border transition ${brandFilter === 'all'
                ? 'bg-stone-800 text-white border-stone-800'
                : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'}`}
              data-testid="filter-brand-all">
              Semua
            </button>
            {brandOptions.map((b) => (
              <button key={b}
                onClick={() => setBrandFilter(b)}
                className={`text-xs px-3 py-1.5 rounded-md border transition ${brandFilter === b
                  ? 'bg-stone-800 text-white border-stone-800'
                  : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'}`}
                data-testid={`filter-brand-${b}`}>
                {b}
              </button>
            ))}
          </div>
        )}

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Cari nama produk..." className="pl-9 h-10"
            data-testid="input-search-produk" />
          {query && (
            <button onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-stone-100">
              <X className="w-3.5 h-3.5 text-stone-500" />
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-stone-500 py-10 text-center">Memuat…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-stone-300 p-10 text-center text-sm text-stone-500">
            {products.length === 0
              ? <>Belum ada produk. Buka <b>Kalkulator</b> untuk hitung + simpan produk pertama.</>
              : <>Tidak ada produk cocok pencarian "<b>{query}</b>".</>}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block rounded-xl border border-stone-200 bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <Th>Jenis</Th>
                    <Th>Merek</Th>
                    <Th>Produk</Th>
                    <Th className="text-center">Paket</Th>
                    <Th className="text-right">Modal</Th>
                    <Th className="text-right">Shopee</Th>
                    <Th className="text-right">TikTok</Th>
                    <Th className="text-right">Minimo</Th>
                    <Th className="text-right w-32">Aksi</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const vs = p.variants || [];
                    const ptype = p.product_type || 'lainnya';
                    return (
                      <tr key={p.id} className="border-b border-stone-100 hover:bg-stone-50/60"
                        data-testid={`row-produk-${p.id}`}>
                        <td className="px-3 py-3">
                          <span className={`inline-block text-[10px] px-2 py-0.5 rounded border font-medium ${TYPE_BADGE[ptype] || TYPE_BADGE.lainnya}`}>
                            {TYPE_LABEL[ptype] || ptype}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          {p.brand ? (
                            <span className="text-xs font-medium text-stone-700">{p.brand}</span>
                          ) : (
                            <span className="text-[10px] text-stone-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <button onClick={() => setDetail(p)}
                            className="text-left font-medium text-stone-900 hover:text-emerald-800 hover:underline"
                            data-testid={`btn-nama-${p.id}`}>
                            {p.name}
                          </button>
                          <div className="text-[10px] text-stone-400 mt-0.5">
                            Diperbarui {formatDate(p.updated_at)}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {p.has_variants && vs.length > 1 ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded border border-emerald-200">
                              <Layers className="w-3 h-3" /> {vs.length} Paket
                            </span>
                          ) : (
                            <span className="text-[10px] text-stone-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-stone-700">{formatRp(p.base_cost || p.modal || 0)}</td>
                        <td className="px-3 py-3 text-right">
                          <div className="font-mono text-sm font-semibold text-orange-800">
                            {rangeText(vs.map((v) => v.shopee_selling_price || 0))}
                          </div>
                          <div className="font-mono text-xs font-bold text-emerald-700 mt-0.5">
                            profit {rangeText(vs.map((v) => v.shopee?.profit || 0))}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="font-mono text-sm font-semibold text-slate-800">
                            {rangeText(vs.map((v) => v.tiktok_selling_price || 0))}
                          </div>
                          <div className="font-mono text-xs font-bold text-emerald-700 mt-0.5">
                            profit {rangeText(vs.map((v) => v.tiktok?.profit || 0))}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="font-mono text-sm font-semibold text-blue-800">
                            {rangeText(vs.map((v) => v.minimo_selling_price || 0))}
                          </div>
                          <div className="font-mono text-xs font-bold text-emerald-700 mt-0.5">
                            profit {rangeText(vs.map((v) => v.minimo?.profit || 0))}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <IconBtn title="Edit" onClick={() => onEdit(p)} testid={`edit-${p.id}`}>
                              <Edit3 className="w-3.5 h-3.5" />
                            </IconBtn>
                            <IconBtn title="Duplikat" onClick={() => onDuplicate(p)} testid={`dup-${p.id}`}>
                              <Copy className="w-3.5 h-3.5" />
                            </IconBtn>
                            <IconBtn title="Hitung ulang dengan tarif terbaru" onClick={() => onRecalc(p)} testid={`recalc-${p.id}`}>
                              <RefreshCw className="w-3.5 h-3.5" />
                            </IconBtn>
                            <IconBtn title="Hapus" onClick={() => setConfirmDel(p)} danger testid={`del-${p.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </IconBtn>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {filtered.map((p) => {
                const vs = p.variants || [];
                return (
                  <div key={p.id} className="rounded-xl border border-stone-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <button onClick={() => setDetail(p)} className="text-left font-semibold text-stone-900">
                        {p.brand && <span className="text-xs text-stone-500 font-normal mr-1">{p.brand}</span>}
                        {p.name}
                      </button>
                      <div className="flex gap-1">
                        <IconBtn onClick={() => onEdit(p)}><Edit3 className="w-3.5 h-3.5" /></IconBtn>
                        <IconBtn onClick={() => onDuplicate(p)}><Copy className="w-3.5 h-3.5" /></IconBtn>
                        <IconBtn onClick={() => setConfirmDel(p)} danger><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                      </div>
                    </div>
                    <div className="text-xs text-stone-500 mb-2">
                      {vs.length} paket · Modal <span className="font-mono text-stone-800">{formatRp(p.base_cost || p.modal || 0)}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[10px]">
                      {['shopee', 'tiktok', 'minimo'].map((k) => (
                        <div key={k} className={`rounded-md border px-2 py-1.5 text-center ${META[k].chip}`}>
                          <div className="uppercase tracking-widest opacity-70">{META[k].label}</div>
                          <div className="font-mono">{rangeText(vs.map((v) => v[`${k}_selling_price`] || 0))}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="produk-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">
                  {detail.brand && <span className="text-sm text-stone-500 font-normal mr-2">{detail.brand}</span>}
                  {detail.name}
                </DialogTitle>
                <DialogDescription>
                  Modal Dasar <b className="text-stone-800">{formatRp(detail.base_cost || detail.modal || 0)}</b>
                  &nbsp;· {(detail.variants || []).length} paket · Diperbarui {formatDate(detail.updated_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {(detail.variants || []).map((v, i) => {
                  const totalModal = (detail.base_cost || detail.modal || 0) + (v.addon_cost || 0);
                  return (
                    <div key={v.id || i} className="rounded-lg border border-stone-200 overflow-hidden">
                      <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                        <div className="text-sm font-semibold text-stone-800">
                          {i + 1}. {v.name || 'Paket'}
                        </div>
                        <div className="text-[11px] text-stone-500">
                          Total modal: <span className="font-mono font-semibold text-stone-800">{formatRp(totalModal)}</span>
                          {v.addon_cost > 0 && <span className="ml-2 text-stone-400">(+{formatRp(v.addon_cost)})</span>}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3">
                        {['shopee', 'tiktok', 'minimo'].map((k) => {
                          const Icon = META[k].icon;
                          const positive = (v?.[k]?.profit || 0) >= 0;
                          const price = v[`${k}_selling_price`] || 0;
                          return (
                            <div key={k} className="rounded-md border border-stone-200 overflow-hidden">
                              <div className={`px-2 py-1.5 flex items-center gap-1.5 border-b ${META[k].chip}`}>
                                <Icon className="w-3 h-3" />
                                <div className="text-[11px] font-semibold">{META[k].label}</div>
                              </div>
                              <div className="p-2.5">
                                <div className="text-[9px] uppercase tracking-widest text-stone-500">Harga</div>
                                <div className="text-sm font-mono font-semibold text-stone-800 mb-1">{formatRp(price)}</div>
                                <div className="text-[9px] uppercase tracking-widest text-stone-500">Profit</div>
                                <div className={`text-sm font-bold font-mono ${positive ? 'text-emerald-900' : 'text-red-900'}`}>
                                  {formatRp(v?.[k]?.profit || 0)}
                                </div>
                                <div className={`text-[10px] ${positive ? 'text-emerald-700' : 'text-red-700'}`}>
                                  {(v?.[k]?.margin || 0).toFixed(1)}% margin
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetail(null)}>Tutup</Button>
                <Button onClick={() => { setDetail(null); onEdit(detail); }}
                  className="bg-emerald-800 hover:bg-emerald-900 text-white gap-1.5">
                  <Edit3 className="w-4 h-4" /> Edit Produk
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Hapus Produk?</DialogTitle>
            <DialogDescription>
              Apakah Anda yakin ingin menghapus produk "<b>{confirmDel?.name}</b>" beserta seluruh paketnya? Aksi ini tidak dapat dibatalkan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Batal</Button>
            <Button onClick={onDelete} className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="btn-confirm-del">
              Hapus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AccountingLayout>
  );
}

const Th = ({ children, className = '' }) => (
  <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-widest text-stone-500 ${className}`}>{children}</th>
);

const IconBtn = ({ children, onClick, title, danger, testid }) => (
  <button onClick={onClick} title={title} data-testid={testid}
    className={`p-1.5 rounded-md border transition ${danger
      ? 'border-red-200 text-red-700 hover:bg-red-50'
      : 'border-stone-200 text-stone-700 hover:bg-stone-50'}`}>
    {children}
  </button>
);

const StatCard = ({ label, value, icon: Icon, accent }) => {
  const map = {
    emerald: 'bg-emerald-50 text-emerald-800',
    orange: 'bg-orange-50 text-orange-800',
    slate: 'bg-slate-50 text-slate-800',
    blue: 'bg-blue-50 text-blue-800',
  };
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${map[accent] || 'bg-stone-50 text-stone-800'}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{label}</div>
      </div>
      <div className="text-lg font-bold font-mono text-stone-900">{value}</div>
    </div>
  );
};
